import React, { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { useGetLibraryQuery } from '../../reactWrapper/Redux/apiSlice';
import { ITEM_TYPES, inputClass } from './cbtQuestionRuns.config';

/**
 * Search box over the Role Library's APPROVED specialties, tools or skills
 * (the API only generates questions for approved items).
 *
 *   <ItemPicker itemType="skill" value={item} onChange={setItem} />
 *   value / onChange: { id, name } or null
 */
export default function ItemPicker({ itemType, value, onChange }) {
  const [text, setText] = useState(value ? value.name : '');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  // Show the picked name when the selection changes from outside
  useEffect(() => {
    setText(value ? value.name : '');
  }, [value]);

  // Wait until the admin stops typing for 300 ms before searching
  useEffect(() => {
    const timer = setTimeout(() => setSearch(text.trim()), 300);
    return () => clearTimeout(timer);
  }, [text]);

  const { data, isFetching } = useGetLibraryQuery(
    { kind: itemType, status: 'approved', search: search, limit: 20 },
    { skip: !open },
  );
  const items = data && data.data ? data.data : [];

  const type = ITEM_TYPES.find((item) => item.key === itemType);
  const plural = type ? type.plural : 'items';

  function pick(item) {
    onChange({ id: item.id, name: item.name });
    setText(item.name);
    setOpen(false);
  }

  function close() {
    // Leaving the box without picking puts the current choice back
    setText(value ? value.name : '');
    setOpen(false);
  }

  return (
    <div className="relative">
      <input
        type="text"
        className={`${inputClass} pr-9`}
        placeholder={`Search approved ${plural}…`}
        value={text}
        autoComplete="off"
        onFocus={(event) => {
          event.target.select();
          setOpen(true);
        }}
        onChange={(event) => {
          setText(event.target.value);
          setOpen(true);
        }}
        onBlur={close}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            close();
          }
        }}
      />
      <Search className="pointer-events-none absolute right-3 top-3 h-4 w-4 text-muted-foreground" />

      {open && (
        <ul className="absolute left-0 right-0 z-30 mt-1 max-h-72 overflow-y-auto rounded-lg border bg-card p-1 shadow-ui-lg">
          {items.map((item) => (
            <li
              key={item.id}
              // onMouseDown fires before the input's onBlur, so the pick is not lost
              onMouseDown={(event) => {
                event.preventDefault();
                pick(item);
              }}
              className="cursor-pointer truncate rounded-md px-2.5 py-2 text-sm hover:bg-brand-navy-100"
            >
              {item.name}
            </li>
          ))}

          {items.length === 0 && (
            <li className="px-2.5 py-2 text-sm text-muted-foreground">
              {isFetching ? 'Searching…' : `No approved ${plural} match "${text}".`}
            </li>
          )}

          {data && data.pagination && data.pagination.total > items.length && (
            <li className="px-2.5 py-2 text-xs text-muted-foreground">
              Showing {items.length} of {data.pagination.total}. Keep typing to narrow it down.
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
